import React, { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import locale from "date-fns/locale/pt-BR";

import useApi from "../../hooks/useApi";
import Modal from "../../components/UI/Modal";
import ReadMoreButton from "../../components/UI/ReadMoreButton";

import {
  Container,
  Section,
  SectionTitle,
  InfoRow,
  Label,
  Value,
  ListContainer,
  ListItem,
  SummaryGrid,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
  ActivityBlock,
  ActivityName,
  ActivityRow,
  ActivityLabel,
  ActivityValue,
  StatusText,
  LoadingContainer,
  ErrorContainer,
} from "./styles";

const formatDate = (timestamp) => {
  if (!timestamp) return "-";
  return format(new Date(parseInt(timestamp)), "dd/MM/yyyy", { locale });
};

const formatTime = (timestamp) => {
  if (!timestamp) return "-";
  return format(new Date(parseInt(timestamp)), "HH:mm", { locale });
};

const formatDateStr = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return format(date, "dd/MM/yyyy", { locale });
};

const getStatusInfo = (hours_taken, activity_score) => {
  if (hours_taken === null || hours_taken === undefined) {
    return { text: "SEM REGISTRO", status: "noRecord" };
  }
  if (hours_taken === 0) {
    return { text: "ATIVIDADE NÃO REALIZADA", status: "notDone" };
  }
  if (hours_taken === 1) {
    return {
      text: `ATIVIDADE REALIZADA - HORAS COMPUTADAS: ${activity_score}`,
      status: "registered",
    };
  }
  if (hours_taken === 2) {
    return { text: "HORAS ABONADAS", status: "justified" };
  }
  return { text: `HORAS COMPUTADAS: ${hours_taken}`, status: "registered" };
};

const CoursesSubscriptionDetails = ({
  open,
  onClose,
  idUserSubscription,
  idContent,
}) => {
  const [details, setDetails] = useState(null);

  const [loadDetails, loadDetailsInfo] = useApi({
    debounceDelay: 1000,
    url: "canalPedagogico/course/subscription/details",
    method: "get",
    onCompleted: (response) => {
      if (!response.error) {
        console.log("Subscription Details", response.data);
        setDetails(response.data);
      }
    },
  });

  useEffect(() => {
    if (open && idUserSubscription && idContent) {
      setDetails(null);
      loadDetails({
        params: {
          idUserSubscription,
          idContent,
        },
      });
    }
  }, [open, idUserSubscription, idContent]);

  const handleClose = useCallback(() => {
    setDetails(null);
    onClose();
  }, [onClose]);

  return (
    <Modal open={open} buttonClose={true} close={handleClose}>
      <Container>
        {loadDetailsInfo.loading && (
          <LoadingContainer>Carregando detalhes da inscrição...</LoadingContainer>
        )}

        {!loadDetailsInfo.loading && !details && !loadDetailsInfo.error && (
          <LoadingContainer>Nenhum dado encontrado.</LoadingContainer>
        )}

        {!loadDetailsInfo.loading && loadDetailsInfo.error && (
          <ErrorContainer>
            Erro ao carregar detalhes: {loadDetailsInfo.error}
          </ErrorContainer>
        )}

        {details && (
          <>
            {/* Dados do curso/evento */}
            <Section>
              <SectionTitle>Dados do curso/evento</SectionTitle>
              <InfoRow>
                <Label>Título:</Label>
                <Value>{details.title || "-"}</Value>
              </InfoRow>
              <InfoRow>
                <Label>Área:</Label>
                <Value>{details.segmentName || "-"}</Value>
              </InfoRow>
              <InfoRow>
                <Label>Categoria:</Label>
                <Value>{details.courseTypeName || "-"}</Value>
              </InfoRow>
              <InfoRow>
                <Label>Tutores:</Label>
                <Value>
                  {details.tutors && details.tutors.length > 0 ? (
                    <ListContainer>
                      {details.tutors.map((tutor, index) => (
                        <ListItem key={tutor.idTutor || index}>
                          {tutor.name}
                        </ListItem>
                      ))}
                    </ListContainer>
                  ) : (
                    "-"
                  )}
                </Value>
              </InfoRow>
              <InfoRow>
                <Label>Encontros:</Label>
                <Value>
                  {details.meetings && details.meetings.length > 0 ? (
                    <ListContainer>
                      {details.meetings.map((meeting, index) => (
                        <ListItem key={meeting.id || index}>
                          {meeting.place || "-"} -{" "}
                          {formatDate(meeting.eventDate)} das{" "}
                          {formatTime(meeting.eventBegin)} às{" "}
                          {formatTime(meeting.eventEnd)}
                        </ListItem>
                      ))}
                    </ListContainer>
                  ) : (
                    "-"
                  )}
                </Value>
              </InfoRow>
            </Section>

            {/* Resumo da carga horária */}
            {details.sumaryOfHours && details.sumaryOfHours.length > 0 && (
              <Section>
                <SectionTitle>Resumo da carga horária</SectionTitle>
                <SummaryGrid>
                  <SummaryItem>
                    <SummaryLabel>Carga horária do curso:</SummaryLabel>
                    <SummaryValue>
                      {details.sumaryOfHours[0].courseWorkload ?? "-"}h
                    </SummaryValue>
                  </SummaryItem>
                  <SummaryItem>
                    <SummaryLabel>Carga horária realizada:</SummaryLabel>
                    <SummaryValue>
                      {details.sumaryOfHours[0].hoursCompleted ?? "-"}h
                    </SummaryValue>
                  </SummaryItem>
                  <SummaryItem>
                    <SummaryLabel>Carga horária abonada:</SummaryLabel>
                    <SummaryValue>
                      {details.sumaryOfHours[0].hoursJustified ?? "-"}h
                    </SummaryValue>
                  </SummaryItem>
                  <SummaryItem>
                    <SummaryLabel>Carga horária não realizada:</SummaryLabel>
                    <SummaryValue>
                      {details.sumaryOfHours[0].uncompletedHours ?? "-"}h
                    </SummaryValue>
                  </SummaryItem>
                  <SummaryItem>
                    <SummaryLabel>Carga horária não lançada:</SummaryLabel>
                    <SummaryValue>
                      {details.sumaryOfHours[0].pendingHours ?? "-"}h
                    </SummaryValue>
                  </SummaryItem>
                </SummaryGrid>
              </Section>
            )}

            {/* Atividades */}
            {details.userActivities && details.userActivities.length > 0 && (
              <Section>
                <SectionTitle>Atividades</SectionTitle>
                {details.userActivities.map((activity, index) => {
                  const statusInfo = getStatusInfo(activity.hours_taken, activity.activity_score);
                  return (
                    <ActivityBlock key={activity.iduser_content_activity || index}>
                      <ActivityName>{activity.activityName || "-"}</ActivityName>
                      <ActivityRow>
                        <ActivityLabel>Módulo:</ActivityLabel>
                        <ActivityValue>{activity.module ?? "-"}</ActivityValue>
                      </ActivityRow>
                      <ActivityRow>
                        <ActivityLabel>Carga horária da atividade:</ActivityLabel>
                        <ActivityValue>
                          {activity.activityWorkload ?? "-"}h
                        </ActivityValue>
                      </ActivityRow>
                      <ActivityRow>
                        <ActivityLabel>Início da atividade:</ActivityLabel>
                        <ActivityValue>
                          {formatDateStr(activity.activityBegin)}
                        </ActivityValue>
                      </ActivityRow>
                      <ActivityRow>
                        <ActivityLabel>Encerramento da atividade:</ActivityLabel>
                        <ActivityValue>
                          {formatDateStr(activity.activityEnd)}
                        </ActivityValue>
                      </ActivityRow>
                      <ActivityRow>
                        <ActivityLabel>Status:</ActivityLabel>
                        <ActivityValue>
                          <StatusText status={statusInfo.status}>
                            {statusInfo.text}
                          </StatusText>
                        </ActivityValue>
                      </ActivityRow>
                    </ActivityBlock>
                  );
                })}
              </Section>
            )}

            {(!details.userActivities ||
              details.userActivities.length === 0) && (
              <Section>
                <SectionTitle>Atividades</SectionTitle>
                <InfoRow>
                  <Value>Nenhuma atividade encontrada.</Value>
                </InfoRow>
              </Section>
            )}
          </>
        )}
      </Container>
    </Modal>
  );
};

export default CoursesSubscriptionDetails;

